import {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {showResultToastCard} from '@/components';
import {getTranslatedErrorMessage} from '@/utils';

import {useInviteMemberMutation} from '../../hooks';

export interface InviteMemberFormValues {
  email: string;
  roleId: string;
}

export const EMPTY_INVITE_MEMBER_FORM_VALUES: InviteMemberFormValues = {email: '', roleId: ''};

export interface UseInviteMemberFormParams {
  isOpen: boolean;
  onSuccess: () => void;
}

export function useInviteMemberForm({isOpen, onSuccess}: UseInviteMemberFormParams) {
  const {t} = useTranslation(['team', 'common']);
  const form = useForm<InviteMemberFormValues>({
    defaultValues: EMPTY_INVITE_MEMBER_FORM_VALUES,
    mode: 'onTouched'
  });

  // The modal stays mounted across open/close (Modal owns the close animation via `isOpen`),
  // so reset on every open rather than relying on defaultValues from a stale prior submission.
  useEffect(() => {
    if (isOpen) form.reset(EMPTY_INVITE_MEMBER_FORM_VALUES);
  }, [isOpen, form]);

  const inviteMutation = useInviteMemberMutation();

  const handleSend = form.handleSubmit(async values => {
    try {
      await inviteMutation.mutateAsync({email: values.email, roleId: values.roleId});
      showResultToastCard({
        isError: false,
        title: t('inviteModal.resultToast.success.title'),
        description: t('inviteModal.resultToast.success.description', {email: values.email}),
        closeLabel: t('inviteModal.resultToast.close')
      });
      onSuccess();
    } catch (error) {
      showResultToastCard({
        isError: true,
        title: t('inviteModal.resultToast.error.title'),
        description: getTranslatedErrorMessage(error, t),
        closeLabel: t('inviteModal.resultToast.close')
      });
    }
  });

  return {form, handleSend, isSending: inviteMutation.isPending, sendError: inviteMutation.error};
}
